import { ProFormTextArea } from '@ant-design/pro-components'
import type React from 'react'
import { EditableLabel } from '../components/EditableLabel'
import { EditableShell } from '../components/EditableShell'
import { useFieldContext } from '../createAppFormHook'
import { useEditableFieldMode } from '../hooks/useEditableFieldMode'
import { useMergedFieldProps } from '../utils/mergeFieldProps'

export type TextAreaFieldProps = React.ComponentProps<typeof ProFormTextArea>

export function TextAreaField(props: TextAreaFieldProps) {
  const fieldCtx = useFieldContext<string>()
  // Strip `readonly` from rest so it doesn't leak onto the underlying <textarea>
  // — React expects camelCase `readOnly`, lowercase triggers a console.error.
  const { formItemProps, fieldProps, proFieldProps, className, readonly: _readonly, ...rest } = props

  const editable = useEditableFieldMode<string>()

  const merged = useMergedFieldProps({
    fieldCtx,
    editable,
    props,
    fieldDefaults: {
      fieldProps: {
        onChange: (e: unknown) => {
          const value = (e as { target?: { value?: string } }).target?.value ?? (e as string)
          fieldCtx.handleChange(value)
        },
      },
    },
  })

  return (
    <EditableShell
      mode={editable.mode}
      enterEdit={editable.enterEdit}
      exitRead={editable.exitRead}
      className={className}
    >
      <ProFormTextArea
        {...rest}
        label={
          rest.label && (
            <EditableLabel
              mode={editable.mode}
              enterEdit={editable.enterEdit}
              exitRead={editable.exitRead}
              onUndo={editable.undoAndExit}
              canUndo={editable.canUndo}
              showActions={!props.readonly}
            >
              {rest.label}
            </EditableLabel>
          )
        }
        proFieldProps={merged.proFieldProps}
        formItemProps={merged.formItemProps}
        fieldProps={merged.fieldProps}
      />
    </EditableShell>
  )
}
